import { HttpApiService } from "@/shared/services/httpApi.service";

import type { CreatePaymentFeePayload, ListPaymentFeesParams, PaymentFee } from "../types/paymentFees.types";
import { PAYMENTS_CONFIG_MAX_LIMIT, toQuery, type PaginatedResponse } from "./paymentsConfig.http";

/**
 * Price book of the payments runtime — coloca-nest `InternalPaymentFeesController`
 * (`payment-config.internal.controller.ts`, tag "🧾 Payments — Fees (Internal)"). Every endpoint is
 * `userTypes: [INTERNAL]`.
 *
 * The full surface is exactly three operations — there is **no PUT/PATCH and no activate/deactivate**:
 *
 * - `GET /internal/payments/fees` (`payments:read`) — paginated (`skip`/`limit`, default 10, **max 200**),
 *   ordered `created_at DESC` (no sort param). Server-side filters: `fee_kind`, `provider_code`,
 *   `organization_id`, `rail`, `country`, `currency`, `direction`, `is_active`. No free-text search, no date
 *   range.
 * - `POST /internal/payments/fees` (`payments:write`) — append-only. `version` is computed server-side as
 *   "latest version of the identical scope tuple + 1", `created_by_user_id` comes from the JWT and
 *   `is_active` is always `true`. A row whose scope AND `valid_from` instant already exist violates
 *   `uniq_payment_fees_version` (NULLS NOT DISTINCT) → **400 `errors.payment.fee_exists`**.
 * - `DELETE /internal/payments/fees/:id` (`payments:write`) — soft delete, 204. `404
 *   errors.payment.fee_not_found` when the id is unknown.
 *
 * Superseding a price is a new row with a later `valid_from`; the previous row's `valid_to` is NOT closed, so
 * "which row wins" is derived client-side (see `utils/feeMath.ts`).
 *
 * RBAC nuance shared with the rest of the runtime surfaces: a missing permission surfaces as **401** (the
 * guard throws Unauthorized) and a wrong `user_type` as **403** — both must latch as no-access via
 * `HttpApiService.isAccessDeniedError`.
 */

const FEES_URL = "/internal/payments/fees";

/** Backend `PaginationDto.Max(200)`. Requesting more is a 400, not a silent clamp. */
export const FEES_MAX_LIMIT = PAYMENTS_CONFIG_MAX_LIMIT;

export const PaymentFeesService = {
  async list(params: ListPaymentFeesParams = {}): Promise<PaginatedResponse<PaymentFee>> {
    const { data } = await HttpApiService.get<PaginatedResponse<PaymentFee>>(
      `${FEES_URL}${toQuery({ ...params })}`,
    );
    return data;
  },

  /**
   * Pages through the whole price book at the backend maximum. Coverage and margin are computed by replaying
   * the resolver over the loaded rows, so a partial set would under-report — the caller must disclose
   * `truncated` instead of showing a wrong figure (no-silent-caps rule).
   */
  async listAll(
    params: Omit<ListPaymentFeesParams, "skip" | "limit"> = {},
    maxPages = 10,
  ): Promise<{ rows: PaymentFee[]; total: number; truncated: boolean }> {
    const rows: PaymentFee[] = [];
    let skip = 0;
    let total = 0;

    for (let page = 0; page < maxPages; page += 1) {
      const response = await PaymentFeesService.list({ ...params, skip, limit: FEES_MAX_LIMIT });
      rows.push(...response.data);
      total = response.total;
      if (!response.hasMore || response.data.length === 0) {
        return { rows, total, truncated: false };
      }
      skip += FEES_MAX_LIMIT;
    }
    return { rows, total, truncated: rows.length < total };
  },

  async create(payload: CreatePaymentFeePayload): Promise<PaymentFee> {
    const { data } = await HttpApiService.post<PaymentFee, CreatePaymentFeePayload>(FEES_URL, payload);
    return data;
  },

  async remove(id: string): Promise<void> {
    await HttpApiService.delete(`${FEES_URL}/${id}`);
  },
};

export type { PaginatedResponse } from "./paymentsConfig.http";
